import { ChangeEvent, useState } from "react";

import { compareDateTimesGreaterOrEqual } from "@/helpers/dateTimeHelper";

import { DateTimeRange } from "./types";

type DateTimeFilterProps = {
  onSave: (data: DateTimeRange) => void;
  defaultValue?: DateTimeRange;
};

const DateTimeFilter = ({ onSave, defaultValue }: DateTimeFilterProps) => {
  const [fromDate, setFromDate] = useState(defaultValue?.from.date || "");
  const [fromTime, setFromTime] = useState(defaultValue?.from.time || "");
  const [toDate, setToDate] = useState(defaultValue?.to.date || "");
  const [toTime, setToTime] = useState(defaultValue?.to.time || "");
  const [includeNulls, setIncludeNulls] = useState(
    defaultValue?.includeNulls || false
  );

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);

    /// If toDate is empty or fromDate is greater than toDate, update toDate
    if (toDate && newFromDate > toDate) {
      setToDate(newFromDate);
      onSave({
        from: { date: newFromDate, time: fromTime },
        to: { date: newFromDate, time: toTime },
        includeNulls
      });
    } else {
      onSave({
        from: { date: newFromDate, time: fromTime },
        to: { date: toDate, time: toTime },
        includeNulls
      });
    }
  };

  const handleFromTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFromTime = e.target.value;
    setFromTime(newFromTime);

    // If dates are equal and fromTime is greater than toTime, update toTime
    if (fromDate && toDate && fromDate === toDate && newFromTime > toTime) {
      setToTime(newFromTime);
      onSave({
        from: { date: fromDate, time: newFromTime },
        to: { date: toDate, time: newFromTime },
        includeNulls
      });
    } else {
      onSave({
        from: { date: fromDate, time: newFromTime },
        to: { date: toDate, time: toTime },
        includeNulls
      });
    }
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newToDate = e.target.value;
    setToDate(newToDate);
    onSave({
      from: { date: fromDate, time: fromTime },
      to: { date: newToDate, time: toTime },
      includeNulls
    });
  };

  const handleToTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newToTime = e.target.value;

    // Check if end date lessthan start date
    if (compareDateTimesGreaterOrEqual(fromDate, toDate, fromTime, newToTime)) {
      setToTime(fromTime);
      onSave({
        from: { date: fromDate, time: fromTime },
        to: { date: toDate, time: fromTime },
        includeNulls
      });
      return;
    }

    setToTime(newToTime);
    onSave({
      from: { date: fromDate, time: fromTime },
      to: { date: toDate, time: newToTime },
      includeNulls
    });
  };

  const handleIncludeNullsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newIncludeNulls = e.target.checked;
    setIncludeNulls(newIncludeNulls);
    onSave({
      from: { date: fromDate, time: fromTime },
      to: { date: toDate, time: toTime },
      includeNulls: newIncludeNulls
    });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 mt-3">
      <div className="space-y-4">
        {/* From section */}
        <div className="space-y-3">
          <span className="text-sm font-bold">From</span>
          <div className="space-y-2">
            <input
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              className="w-full h-[40px] px-3 border border-gainsboro rounded-md text-sm"
              aria-label="From Date"
            />
            <input
              type="time"
              value={fromTime}
              onChange={handleFromTimeChange}
              className="w-full h-[40px] px-3 border border-gainsboro rounded-md text-sm"
              aria-label="From Time"
            />
          </div>
        </div>

        {/* Until section */}
        <div className="space-y-3">
          <span className="text-sm font-bold">Until</span>
          <div className="space-y-2">
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={handleToDateChange}
              className="w-full h-[40px] px-3 border border-gainsboro rounded-md text-sm"
              aria-label="To Date"
            />
            <input
              type="time"
              value={toTime}
              onChange={handleToTimeChange}
              className="w-full h-[40px] px-3 border border-gainsboro rounded-md text-sm"
              aria-label="To Time"
            />
          </div>
        </div>

        {/* Include Nulls checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNulls}
            onChange={handleIncludeNullsChange}
            className="rounded border-gray-300 w-3.5 h-3.5 accent-primary-teal"
          />
          <span className="text-xs text-txtBody font-semibold">
            Include Nulls
          </span>
        </label>
      </div>
    </div>
  );
};

export default DateTimeFilter;
